import { useFormContext, Controller } from "react-hook-form";
import { FormattedMessage } from "react-intl";
import { useEffect, useLayoutEffect, useRef, useState, MouseEvent } from "react";

import { classNames } from "helpers/commonHelpers";
import Button from "components/v2/Button/Button";
import { ReactComponent as Check } from "assets/images/iconsV2/check.svg";
import { ReactComponent as ChevronDown } from "assets/images/iconsV2/chevron-down.svg";
import { ReactComponent as ChevronUp } from "assets/images/iconsV2/chevron-up.svg";
import { ReactComponent as MenuIcon } from "assets/images/iconsV2/dots-vertical.svg";
import { useDropdownKeyboard } from "./hooks/useDropdownKeyboard";
import "./Dropdown.scss";
import Divider from "../Divider/Divider";

export interface DropdownOption {
  value: string | number;
  label: React.ReactNode;
  sublabel?: string;
  group?: string;
  onClick?: () => void; // If onClick is present, the option will be treated as a button
  variant?: "default" | "error";
  disabled?: boolean;
}

export type DropdownTriggerProps = {
  onClick: (e?: any) => void;
  onFocus: (e: React.FocusEvent<HTMLElement>) => void;
  onBlur: (e: React.FocusEvent<HTMLElement>) => void;
  onKeyDown: (e: React.KeyboardEvent<HTMLElement>) => void;
  "aria-expanded": boolean;
  "aria-haspopup": "listbox";
  "aria-controls"?: string;
  "aria-activedescendant"?: string;
};

export interface DropdownProps {
  /** Array of options to display in the dropdown. Each option must have a value and a label. */
  options: DropdownOption[];

  /** Callback function to be called when an option is selected. Receives the selected value as an argument. */
  onSelect?: (value: any) => void;

  /** Name of the dropdown field, used for form context integration with react-hook-form. */
  name?: string;

  /** Placeholder text to display when no option is selected. */
  placeholder?: string;

  /** Validation rules for the dropdown field, used for form context integration with react-hook-form. */
  registerOptions?: object;

  /** Controlled value for the dropdown. */
  value?: string | number;

  /** Default value for the dropdown. */
  defaultValue?: string;

  /** Maximum width for the dropdown menu. */
  maxWidth?: string;

  /** If true, the dropdown field is required. */
  required?: boolean;

  /** If true, the dropdown will use a menu icon instead of a button. */
  useMenuIcon?: boolean;

  /** If true, the dropdown menu will be aligned to the right. */
  alignRight?: boolean;

  /** Custom error message to display when validation fails. */
  errorMessage?: string;

  /** If true, the dropdown field will be unregistered when unmounted. */
  shouldUnregister?: boolean;

  /** If true, the dropdown menu will expand above the dropdown field. */
  expandAbove?: boolean;

  /** If true, the dropdown menu will flip above when there is not enough space below. */
  autoFlip?: boolean;

  /** If true, menu text wraps and the menu width is constrained to the viewport. */
  wrapMenu?: boolean;

  /** Width of the dropdown menu. This will turn the menu into a fixed width instead of fit content */
  width?: "xs" | "sm" | "md" | "lg"; // For now only xs+sm available

  /** Variant of the dropdown. */
  variant?: "default" | "outline" | "secondary-color";

  /** If true, the button text will have tertiary styling when no value is selected */
  textTertiary?: boolean;

  /** Label for the dropdown */
  label?: React.ReactNode;

  /** If true, the dropdown trigger will be disabled */
  disabled?: boolean;

  /** data-testid for the dropdown trigger (menu icon or button) */
  triggerTestId?: string;

  /** Custom trigger element. Receives handlers and ARIA props for keyboard accessibility. */
  customTrigger?: (props: DropdownTriggerProps) => React.ReactNode;

  /** If true, close the menu when the visibility root leaves the viewport. */
  closeWhenOffscreen?: boolean;

  /** Element whose visibility keeps the menu open. Falls back to the nearest `.v2-card` or trigger. */
  visibilityRootRef?: React.RefObject<Element | null>;
}

type DropdownInnerProps = Omit<DropdownProps, "name" | "registerOptions" | "shouldUnregister" | "defaultValue"> & {
  fieldProps?: { value?: string | number; onChange?: (value: string | number) => void };
  error?: { message?: string } | null;
};

function DropdownInner({
  options,
  onSelect,
  value,
  errorMessage,
  required,
  maxWidth,
  useMenuIcon,
  placeholder,
  alignRight,
  expandAbove,
  autoFlip,
  wrapMenu,
  width,
  variant,
  textTertiary = true,
  label,
  disabled,
  triggerTestId,
  customTrigger,
  closeWhenOffscreen,
  visibilityRootRef,
  fieldProps = {},
  error = null,
}: DropdownInnerProps) {
  const [computedExpandAbove, setComputedExpandAbove] = useState<boolean>(!!expandAbove);
  const [menuStyle, setMenuStyle] = useState<React.CSSProperties>({});
  const [isMenuPositioned, setIsMenuPositioned] = useState<boolean>(false);
  const ref = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const widthClass = width ? `dropdown-menu-${width}` : "";
  const selectedValue = fieldProps.value ?? value;

  const selectOption = (option: DropdownOption) => {
    if (option.disabled) return;
    if (option.onClick) option.onClick();
    else {
      if (fieldProps?.onChange) fieldProps.onChange(option.value);
      if (onSelect) onSelect(option.value);
    }
  };

  const {
    collapsed,
    highlightedIndex,
    menuId,
    optionRefs,
    closeMenu,
    toggleDropdown,
    handleFocus,
    handleBlur,
    handleKeyDown,
    triggerAriaProps,
  } = useDropdownKeyboard({
    options,
    disabled,
    selectedValue,
    onSelectOption: selectOption,
    containerRef: ref,
  });

  const triggerProps: DropdownTriggerProps = {
    onClick: disabled ? () => {} : toggleDropdown,
    onFocus: handleFocus,
    onBlur: handleBlur,
    onKeyDown: handleKeyDown,
    ...triggerAriaProps,
  };

  useEffect(() => {
    const checkIfClickedOutside = (e: Event) => {
      if (collapsed && ref?.current && !ref?.current.contains(e.target as Node)) closeMenu();
    };
    document.addEventListener("click", checkIfClickedOutside);
    return () => document.removeEventListener("click", checkIfClickedOutside);
  }, [collapsed, closeMenu]);

  useEffect(() => {
    if (!collapsed || !closeWhenOffscreen) return;

    const observeTarget =
      visibilityRootRef?.current ?? ref.current?.closest(".v2-card") ?? ref.current;
    if (!observeTarget) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) {
          closeMenu();
        }
      },
      { threshold: 0 },
    );

    observer.observe(observeTarget);

    return () => observer.disconnect();
  }, [collapsed, closeMenu, closeWhenOffscreen, visibilityRootRef]);

  useLayoutEffect(() => {
    if (!collapsed) {
      setComputedExpandAbove(!!expandAbove);
      setMenuStyle({});
      setIsMenuPositioned(false);
      return;
    }

    const updateMenuLayout = () => {
      if (!ref.current || !menuRef.current) return;

      const triggerRect = ref.current.getBoundingClientRect();
      const menuEl = menuRef.current;
      const viewportPadding = 16;
      const availableWidth = Math.max(
        alignRight ? triggerRect.right - viewportPadding : window.innerWidth - triggerRect.left - viewportPadding,
        120,
      );

      if (wrapMenu) {
        menuEl.style.whiteSpace = "nowrap";
        menuEl.style.width = "max-content";
        menuEl.style.maxWidth = "none";
      }

      const menuHeight = menuEl.scrollHeight;
      const spaceBelow = window.innerHeight - triggerRect.bottom - viewportPadding;
      const spaceAbove = triggerRect.top - viewportPadding;
      const wouldBeCutOffBelow = triggerRect.bottom + menuHeight + viewportPadding > window.innerHeight;
      const wouldBeCutOffAbove = triggerRect.top - menuHeight - viewportPadding < 0;

      let expandAboveValue = !!expandAbove;
      if (autoFlip) {
        if (wouldBeCutOffBelow && !wouldBeCutOffAbove) {
          expandAboveValue = true;
        } else if (!wouldBeCutOffBelow && wouldBeCutOffAbove) {
          expandAboveValue = false;
        } else if (wouldBeCutOffBelow) {
          expandAboveValue = spaceAbove > spaceBelow;
        } else {
          expandAboveValue = false;
        }
      }

      setComputedExpandAbove(expandAboveValue);

      const nextMenuStyle: React.CSSProperties = {
        overflowX: "hidden",
      };

      if (autoFlip) {
        const availableHeight = expandAboveValue ? spaceAbove : spaceBelow;
        nextMenuStyle.maxHeight = `${Math.max(Math.min(250, availableHeight), 80)}px`;
      }

      if (wrapMenu) {
        const naturalWidth = menuEl.scrollWidth;
        const menuWidth = Math.min(Math.max(naturalWidth, 120), availableWidth);

        menuEl.style.whiteSpace = "";
        menuEl.style.width = `${menuWidth}px`;
        menuEl.style.maxWidth = `${menuWidth}px`;

        nextMenuStyle.width = `${menuWidth}px`;
        nextMenuStyle.maxWidth = `${menuWidth}px`;
      } else if (maxWidth) {
        nextMenuStyle.maxWidth = maxWidth;
      }

      setMenuStyle(nextMenuStyle);
      setIsMenuPositioned(true);
    };

    updateMenuLayout();

    window.addEventListener("scroll", updateMenuLayout, true);
    window.addEventListener("resize", updateMenuLayout);

    return () => {
      window.removeEventListener("scroll", updateMenuLayout, true);
      window.removeEventListener("resize", updateMenuLayout);
    };
  }, [collapsed, autoFlip, expandAbove, wrapMenu, alignRight, maxWidth, options]);

  const handleSelect = (e: MouseEvent<HTMLDivElement>, option: DropdownOption) => {
    if (e) e.stopPropagation();
    selectOption(option);
    closeMenu();
  };

  const dropdownContainerClass = `dropdownV2 ${variant === "outline" ? "dropdownV2--outline" : ""}`;
  const buttonVariant =
    variant === "outline" ? "link-color" : variant === "secondary-color" ? "secondary-color" : "secondary-gray";
  const menuIconLabel = "Open menu";

  const resolvedError = error ?? (errorMessage ? { message: errorMessage } : null);

  return (
    <div className={dropdownContainerClass} ref={ref}>
      {label && (
        <label className={classNames({ required })}>{label}</label>
      )}
      {customTrigger ? (
        customTrigger(triggerProps)
      ) : useMenuIcon ? (
        <button
          type="button"
          className={classNames("dropdownV2__menu-icon-btn", {
            "dropdownV2__menu-icon-btn--disabled": disabled,
          })}
          disabled={disabled}
          data-testid={triggerTestId}
          aria-label={menuIconLabel}
          {...triggerProps}
        >
          <MenuIcon className="cursor-pointer" />
        </button>
      ) : (
        <Button
          variant={buttonVariant}
          iconRight={collapsed ? <ChevronUp /> : <ChevronDown />}
          rightIconClassName="dropdownV2__chevron"
          testId={triggerTestId}
          disabled={disabled}
          label={
            selectedValue && selectedValue !== ""
              ? options.find((option) => option.value === selectedValue)?.label
              : placeholder
          }
          className={classNames({
            "w-100": variant !== "outline",
            "d-flex justify-content-between": alignRight,
            "text-tertiary": (!selectedValue || selectedValue === "") && textTertiary,
            "dropdownV2__trigger--open": collapsed,
          })}
          onClick={triggerProps.onClick}
          onFocus={triggerProps.onFocus}
          onBlur={triggerProps.onBlur}
          onKeyDown={triggerProps.onKeyDown}
          aria-expanded={triggerProps["aria-expanded"]}
          aria-haspopup={triggerProps["aria-haspopup"]}
          aria-controls={triggerProps["aria-controls"]}
          aria-activedescendant={triggerProps["aria-activedescendant"]}
        />
      )}

      {collapsed && (
        <div
          ref={menuRef}
          id={menuId}
          role="listbox"
          className={`dropdown-menu ${alignRight && "align-right"} ${
            computedExpandAbove ? "expand-above" : "expand-below"
          } ${wrapMenu ? "dropdown-menu--wrap" : ""} ${widthClass}`}
          style={{
            ...menuStyle,
            ...(wrapMenu && !isMenuPositioned ? { visibility: "hidden", overflowX: "hidden" } : {}),
          }}
        >
          {options.map((option, index) => (
            <div
              key={option.value}
              ref={(element) => {
                optionRefs.current[index] = element;
              }}
              id={`${menuId}-option-${index}`}
              role="option"
              tabIndex={-1}
              aria-selected={selectedValue === option.value}
              className={classNames("dropdown-menu-item text-md", {
                "dropdown-menu-item--disabled": option.disabled,
                "dropdown-menu-item--highlighted": highlightedIndex === index,
              })}
              onClick={option.disabled ? undefined : (e) => handleSelect(e, option)}
            >
              {option?.group && options[index - 1]?.group !== option.group && (
                <div>
                  {index !== 0 && <Divider className="dropdown-group-divider" />}
                  <div className="pl-md text-tertiary text-xs font-semibold">{option?.group}</div>
                </div>
              )}
              <div className="dropdown-content">
                <div>
                  <div className={option.variant === "error" ? "text-error-primary" : ""}>{option.label}</div>
                  <div className="text-tertiary text-xs">{option.sublabel}</div>
                </div>
                {selectedValue === option.value && <Check stroke="#005EAC" />}
              </div>
            </div>
          ))}
        </div>
      )}
      {resolvedError && (
        <div className="text-sm error-text text-error-primary">
          {resolvedError.message ? (
            resolvedError.message
          ) : (
            <FormattedMessage id="general.requiredField" defaultMessage="This is a required field" />
          )}
        </div>
      )}
    </div>
  );
}

function Dropdown({
  name,
  registerOptions,
  shouldUnregister,
  defaultValue,
  required,
  ...rest
}: DropdownProps) {
  const formContext = useFormContext();
  const control = formContext ? formContext.control : null;

  if (name && control) {
    return (
      <Controller
        name={name}
        control={control}
        shouldUnregister={shouldUnregister}
        rules={{ required, ...registerOptions }}
        defaultValue={defaultValue}
        render={({ field, fieldState }) => (
          <DropdownInner {...rest} required={required} fieldProps={field} error={fieldState.error} />
        )}
      />
    );
  }

  return <DropdownInner {...rest} required={required} />;
}

Dropdown.defaultProps = {
  onSelect: () => {},
  name: "",
  registerOptions: {},
  required: false,
  shouldUnregister: false,
  value: "",
  defaultValue: "",
  maxWidth: "none",
  alignRight: false,
  useMenuIcon: false,
  placeholder: "Select",
  errorMessage: "",
  expandAbove: false,
  autoFlip: false,
  wrapMenu: false,
  width: undefined,
  variant: undefined,
  textTertiary: true,
  label: null,
  disabled: false,
  triggerTestId: undefined,
  customTrigger: undefined,
  closeWhenOffscreen: false,
  visibilityRootRef: undefined,
};

export default Dropdown;
